import { dependencyTypeFromDhtmlx, dependencyTypeToDhtmlx } from '../utils/utils';

export type DependencyApiType = 'FS' | 'SS' | 'FF' | 'SF';

export type ParsedDependencyToken = {
  /** Task lookup key (Sr No / WBS / name / id) without type/lag suffix. */
  taskKey: string;
  type: DependencyApiType;
  /** Days; negative = lead. */
  lag: number;
};

const TYPE_SET = new Set(['FS', 'SS', 'FF', 'SF']);

function normalizeApiType(raw: string | null | undefined): DependencyApiType {
  if (!raw) return 'FS';
  const s = String(raw).trim();
  if (/^[0-3]$/.test(s)) {
    return dependencyTypeFromDhtmlx(s) as DependencyApiType;
  }
  const upper = s.toUpperCase();
  return TYPE_SET.has(upper) ? (upper as DependencyApiType) : 'FS';
}

/**
 * Format a dependency for grid / panel display.
 * Examples: `1.2FS`, `1.2SS+2`, `1.2FF-1`
 */
export function formatDependencyLinkToken(
  taskCode: string,
  type: string | null | undefined,
  lag: number | null | undefined,
): string {
  const code = String(taskCode ?? '').trim();
  if (!code) return '';
  const apiType = normalizeApiType(type);
  const lagNum = Number.isFinite(Number(lag)) ? Math.trunc(Number(lag)) : 0;
  const lagPart = lagNum === 0 ? '' : lagNum > 0 ? `+${lagNum}` : String(lagNum);
  return `${code}${apiType}${lagPart}`;
}

/**
 * Parse one predecessor/successor token that may include type and lag.
 * Accepts `1.2`, `1.2FS`, `1.2FS+2`, `1.2 SS -1`.
 */
export function parseDependencyToken(token: string): ParsedDependencyToken | null {
  const raw = String(token ?? '').trim();
  if (!raw) return null;

  const withType = raw.match(/^(.*?)(?:\s*)(FS|SS|FF|SF)(?:\s*)([+-]\d+)?$/i);
  if (withType && withType[1].trim()) {
    return {
      taskKey: withType[1].trim(),
      type: normalizeApiType(withType[2]),
      lag: withType[3] ? Number(withType[3]) : 0,
    };
  }

  const withLagOnly = raw.match(/^(.*?)([+-]\d+)$/);
  if (withLagOnly && withLagOnly[1].trim()) {
    return {
      taskKey: withLagOnly[1].trim(),
      type: 'FS',
      lag: Number(withLagOnly[2]),
    };
  }

  return { taskKey: raw, type: 'FS', lag: 0 };
}

/** Split a comma/semicolon list into dependency tokens (preserves type/lag per token). */
export function parseDependencyTokenList(input: string): ParsedDependencyToken[] {
  return String(input ?? '')
    .split(/[,;]+/)
    .map((part) => parseDependencyToken(part))
    .filter((t): t is ParsedDependencyToken => Boolean(t?.taskKey));
}

/** DHTMLX link type string ("0"–"3") from a parsed/API type. */
export function dependencyTokenToDhtmlxType(type: DependencyApiType | string): string {
  return dependencyTypeToDhtmlx(normalizeApiType(type));
}
